import { chipInteractionHint } from '#components/chip-interaction-hint/chip-interaction-hint.module.css';

import { DrawingProperties } from '@cadstrom/renderer';
import { SchLayerType } from '@cadstrom/sigma-engine-shared';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { use, useCallback, useMemo, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import { useActiveDesignContext } from '#components/cad-renderer/context/active-design-context.js';
import {
	CadstromColorProviderContext,
	graphicRenderOptsToCss,
	useColorForLayer,
} from '#components/cad-renderer/context/cadstrom-color-provider-context.js';
import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { InteractionManagerContext } from '#components/cad-renderer/context/interaction-manager-context.js';
import { useZIndex } from '#components/cad-renderer/context/z-index-context.js';
import { moduleContainsPin, resolveZoomSelection } from '#components/cad-renderer/module-zoom-selection.js';
import {
	dismissChipInteractionHint,
	useChipInteractionHint,
} from '#components/chip-interaction-hint/chip-interaction-hint-store.js';
import { ChipInteractionHintTooltipContent } from '#components/chip-interaction-hint/chip-interaction-hint-tooltip-content.js';
import { ChipInteractionHint } from '#components/chip-interaction-hint/chip-interaction-hint.js';
import { SmartPopover } from '#components/smart-popover.js';
import { theme } from '#src/theme.js';
import { CadRendererSetActiveDesignEvent, CadRendererZoomEvent, dispatchEventAsync } from '#utils/events.js';
import { useIsModuleDnp } from '#utils/use-is-module-dnp.js';
import { ModelModuleChipContextMenu } from './context-menu/model-module-chip-context-menu.js';
import { resolveModuleZoomTarget } from './model-module-chip-utils.js';
import { ModuleChipAdornment } from './module-chip-adornment.js';
import { useModuleSchematicInstances, useProjectModule } from './use-project-module.js';

import type { DrawingPropertyValue } from '@cadstrom/renderer';
import type { SignalId } from '@cadstrom/sigma-engine-shared';
import type { ChipProps } from '@mui/material/Chip';

export interface ModelModuleChipProps {
	moduleReferenceDesignator: string;
	symbolPin?: string;
	footprintPad?: string;
	signalId?: SignalId;
	selected?: boolean;
	highlighted?: boolean;
	xToDeselect?: boolean;
	/** The name of the design sheet if the module is on a different one than the current. */
	designSheet?: string;
	/** Additional menu items to render in the context menu - function receives closeMenu callback */
	renderAdditionalMenuItems?: (closeMenu: () => void) => React.ReactNode;
	className?: string;
	chipProps?: ChipProps;

	storybook_forceHoverState?: boolean;
	/**
	 * Overrides the attention pulse: `true` always pulses (with the instant tooltip), `false` never
	 * pulses. Leave undefined for the default one-chip-at-a-time behaviour.
	 */
	storybook_forceInteractionHint?: boolean;
}

/**
 * A chip component that displays module reference designators with optional signal names.
 */
export function ModelModuleChip({
	moduleReferenceDesignator: moduleReferenceDesignatorProp,
	selected: forceSelected,
	highlighted: forceHighlighted,
	xToDeselect,
	renderAdditionalMenuItems,
	className,
	chipProps,
	storybook_forceHoverState,
	storybook_forceInteractionHint,
	...props
}: ModelModuleChipProps) {
	invariant(
		!((props.symbolPin !== undefined || props.footprintPad !== undefined) && props.signalId !== undefined),
		'Cannot specify both symbolPin and footprintPad and signalName',
	);

	const { coreModel } = useCoreModelContext(false) ?? {};
	const designContext = useActiveDesignContext(false);
	const colorProviders = use(CadstromColorProviderContext);

	const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<HTMLElement | null>(null);
	const contextMenuOpen = Boolean(contextMenuAnchorEl);
	const chipRef = useRef<HTMLDivElement>(null);
	const contextMenuZIndex = useZIndex('context-menu');

	const interactionManagerContext = use(InteractionManagerContext);

	const symbolColorOpts = useColorForLayer(colorProviders?.schematic, SchLayerType.Symbol);
	const symbolColor = symbolColorOpts ? graphicRenderOptsToCss(symbolColorOpts.selectionNeutral) : '#444444';

	const projectModule = useProjectModule(moduleReferenceDesignatorProp);
	const projectModuleReferenceDesignator = projectModule?.name ?? moduleReferenceDesignatorProp;
	const moduleInstances = useModuleSchematicInstances(projectModule);
	const isDnp = useIsModuleDnp(projectModuleReferenceDesignator, projectModule);

	// When this chip targets a specific pin/pad/signal, only the instances that actually contain those
	// pins are relevant — a multi-unit part splits its pins across units (U1A/U1B), so listing every
	// instance (or defaulting navigation to the first) is misleading. Filter to the instances whose
	// symbol declares one of the target pins.
	const relevantInstances = useMemo(() => {
		const pinSpecified =
			props.symbolPin !== undefined || props.footprintPad !== undefined || props.signalId !== undefined;
		if (!pinSpecified || !coreModel || !projectModule) {
			return moduleInstances;
		}

		if (props.symbolPin !== undefined) {
			return moduleInstances.filter((m) => moduleContainsPin(m, coreModel.components, props.symbolPin!, 'symbolPin'));
		}

		if (props.footprintPad !== undefined) {
			return moduleInstances.filter((m) =>
				moduleContainsPin(m, coreModel.components, props.footprintPad!, 'footprintPad'),
			);
		}

		if (props.signalId !== undefined) {
			const signal = coreModel.components[projectModule.componentRef]?.signals.find((s) => s.id === props.signalId);
			const usesPads = designContext?.activeDesign.modelType === 'pcb';
			const refs = usesPads ? (signal?.padRefs ?? []) : (signal?.pinRefs.filter((r): r is string => r !== null) ?? []);
			if (refs.length === 0) {
				return [];
			}
			const kind = usesPads ? 'footprintPad' : 'symbolPin';
			return moduleInstances.filter((m) => refs.some((ref) => moduleContainsPin(m, coreModel.components, ref, kind)));
		}

		return [];
	}, [moduleInstances, props.symbolPin, props.footprintPad, props.signalId, coreModel, projectModule, designContext]);

	const signalName: string | undefined = useMemo(() => {
		if (!projectModule) {
			return undefined;
		}

		const component = coreModel?.components[projectModule.componentRef];
		if (!component) {
			return undefined;
		}

		if (props.symbolPin !== undefined) {
			return component.signals.find((s) => s.pinRefs.includes(props.symbolPin!))?.name;
		}

		if (props.footprintPad !== undefined) {
			return component.signals.find((s) => s.padRefs.includes(props.footprintPad!))?.name;
		}

		if (props.signalId !== undefined) {
			return component.signals.find((s) => s.id === props.signalId)?.name;
		}

		return undefined;
	}, [projectModule, coreModel?.components, props.symbolPin, props.footprintPad, props.signalId]);

	// The set of graphics this chip targets. For a pin/pad/signal, scoped to the exact compound
	// `module/ref` keys the renderer uses — so highlight and selection always agree with each other.
	const interactionMap = useMemo<Map<DrawingProperties, Set<DrawingPropertyValue>>>(() => {
		const map = new Map<DrawingProperties, Set<DrawingPropertyValue>>();
		const pinSpecified =
			props.symbolPin !== undefined || props.footprintPad !== undefined || props.signalId !== undefined;

		if (pinSpecified) {
			const activeDesign = designContext?.activeDesign;
			if (activeDesign && coreModel) {
				const commands = resolveZoomSelection(
					{
						moduleReferenceDesignator: projectModuleReferenceDesignator,
						symbolPin: props.symbolPin,
						footprintPad: props.footprintPad,
						signalId: props.signalId,
					},
					activeDesign,
					coreModel.components,
				);
				for (const { property, value } of commands) {
					const values = map.get(property) ?? new Set<DrawingPropertyValue>();
					values.add(value);
					map.set(property, values);
				}
			}
		} else {
			const activeDesign = designContext?.activeDesign;
			if (activeDesign && coreModel) {
				const commands = resolveZoomSelection(
					{ moduleReferenceDesignator: projectModuleReferenceDesignator },
					activeDesign,
					coreModel.components,
				);
				for (const { property, value } of commands) {
					const values = map.get(property) ?? new Set<DrawingPropertyValue>();
					values.add(value);
					map.set(property, values);
				}
			} else {
				map.set(DrawingProperties.ObjectId, new Set<DrawingPropertyValue>([projectModuleReferenceDesignator]));
			}
		}

		return map;
	}, [
		props.symbolPin,
		props.footprintPad,
		props.signalId,
		projectModuleReferenceDesignator,
		designContext?.activeDesign,
		coreModel,
	]);

	// This chip shows emphasis when any of its target graphics appear in the interaction manager's maps.
	const isHighlightedByInteractionManager = interactionMapOverlaps(
		interactionMap,
		interactionManagerContext?.highlight,
	);
	const isSelectedByInteractionManager = interactionMapOverlaps(interactionMap, interactionManagerContext?.selection);
	const isSelected = forceSelected === true || isSelectedByInteractionManager;
	const isHighlighted = forceHighlighted === true || isHighlightedByInteractionManager;

	const { pulse, observe: observeChipForHint } = useChipInteractionHint({
		eligible: storybook_forceInteractionHint !== false && chipProps?.disabled !== true && !isSelected,
	});
	const hintPulse = storybook_forceInteractionHint ?? pulse;

	// The chip element both anchors the context menu and is observed for viewport visibility.
	const setChipNode = useCallback(
		(node: HTMLDivElement | null) => {
			chipRef.current = node;
			observeChipForHint(node);
		},
		[observeChipForHint],
	);

	const setHighlight = useCallback(
		(isHovered: boolean) => {
			interactionManagerContext?.setHighlight(
				isHovered ? interactionMap : new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
			);
		},
		[interactionManagerContext, interactionMap],
	);

	const setSelection = useCallback(
		(isSelected: boolean) => {
			interactionManagerContext?.setSelection(
				isSelected ? interactionMap : new Map<DrawingProperties, Set<DrawingPropertyValue>>(),
			);
		},
		[interactionManagerContext, interactionMap],
	);

	const handleDeselect = useCallback(() => {
		setSelection(false);
	}, [setSelection]);

	const handleMouseEnter = useCallback(() => {
		setHighlight(true);
	}, [setHighlight]);

	const handleMouseLeave = useCallback(() => {
		// Don't clear highlight if context menu is open - let the user interact with the popper
		if (!contextMenuOpen) {
			setHighlight(false);
		}
	}, [contextMenuOpen, setHighlight]);

	const handleContextMenuClose = useCallback(() => {
		setContextMenuAnchorEl(null);
		// Clear highlight when context menu closes, since mouse is likely no longer over the chip
		setHighlight(false);
	}, [setHighlight]);

	const handleZoomToItem = useCallback(
		async (moduleReferenceDesignator: string) => {
			handleContextMenuClose();

			// dispatch events sequentially
			await dispatchEventAsync(
				new CadRendererSetActiveDesignEvent({
					moduleReferenceDesignator,
					signalId: props.signalId,
					symbolPin: props.symbolPin,
					footprintPad: props.footprintPad,
				}),
			);
			await dispatchEventAsync(
				new CadRendererZoomEvent({
					moduleReferenceDesignator,
					signalId: props.signalId,
					symbolPin: props.symbolPin,
					footprintPad: props.footprintPad,
				}),
			);
		},
		[handleContextMenuClose, props],
	);

	const handleClick = useCallback(() => {
		dismissChipInteractionHint();
		setSelection(true);

		// If we don't have a projectModule, try zooming to the provided reference designator
		// as a fallback, but this shouldn't happen
		if (projectModule === undefined) {
			void handleZoomToItem(projectModuleReferenceDesignator);
			return;
		}

		// When a specific pin is specified (symbolPin, signalId, or footprintPad), keep the project-level
		// module name so that the SetActiveDesign event handler can resolve the correct instance via pin
		// matching. Overriding with an instance name here would cause navigation to fail when the user is
		// currently on a sheet that contains the wrong instance (e.g. user on U1B's sheet but pin belongs to U1A).
		const pinSpecified =
			props.symbolPin !== undefined || props.signalId !== undefined || props.footprintPad !== undefined;

		const targetReferenceDesignator =
			pinSpecified ?
				projectModule.name
			:	resolveModuleZoomTarget(projectModule, designContext?.activeDesign.designId, coreModel?.designs ?? {});

		void handleZoomToItem(targetReferenceDesignator);
	}, [
		setSelection,
		projectModuleReferenceDesignator,
		projectModule,
		handleZoomToItem,
		props.symbolPin,
		props.signalId,
		props.footprintPad,
		designContext?.activeDesign.designId,
		coreModel?.designs,
	]);

	const handleRightClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		dismissChipInteractionHint();
		setContextMenuAnchorEl(chipRef.current);
	}, []);

	const signalDisplayText = signalName ?? props.symbolPin ?? props.footprintPad;
	return (
		<>
			<Tooltip
				title={<ChipInteractionHintTooltipContent />}
				enterDelay={hintPulse ? 0 : 1000}
				enterNextDelay={hintPulse ? 0 : 1000}
				disableInteractive
			>
				<span className={clsx('relative leading-0', className)} style={{ '--chip-color': symbolColor }}>
					<Chip
						component='button'
						type='button'
						variant={isSelected || isHighlighted ? 'filled' : 'outlined'}
						{...chipProps}
						ref={setChipNode}
						label={
							<>
								<span>{moduleReferenceDesignatorProp}</span>
								{signalDisplayText && (
									<>
										<span
											className={clsx(
												'relative -z-1 mx-1 flex w-1.75 items-stretch self-stretch',
												'dark:text-[oklch(from_currentcolor_calc(l*0.25)_calc(c*0.3)_h/0.5)]',
												'light:text-[oklch(from_currentcolor_70%_100%_h/0.15)]',
											)}
										>
											<span className='grow bg-current [clip-path:polygon(0%_0%,100%_50%,0%_100%)]' />
											<span className='absolute top-0 right-full h-full w-20 self-stretch bg-current' />
										</span>
										<span>{signalDisplayText}</span>
									</>
								)}
							</>
						}
						icon={<ModuleChipAdornment hasMultipleInstances={relevantInstances.length > 1} />}
						data-force-hover-state={storybook_forceHoverState}
						className={clsx(
							'isolate overflow-hidden',
							(isSelected || isHighlighted) &&
								'shadow-[-0.5px_1px_6px_oklch(from_var(--chip-shadow-color)_l_c_h/calc(alpha*0.6))]',
							hintPulse && !isHighlighted && chipInteractionHint,
							// A hatched background marks DNP (do-not-populate) modules. The stripes use
							// currentcolor, so they contrast with either variant's background.
							isDnp &&
								'bg-[repeating-linear-gradient(135deg,transparent,transparent_3px,oklch(from_currentcolor_l_c_h/0.25)_3px,oklch(from_currentcolor_l_c_h/0.25)_4.5px)]',
							chipProps?.className,
						)}
						classes={{
							...chipProps?.classes,
							label: clsx('flex items-center self-stretch', chipProps?.classes?.label),
							outlined: clsx(
								'text-(--chip-color)',
								'dark:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] dark:bg-[oklch(from_var(--chip-color)_l_c_h/0.2)]',
								'dark:hover:border-[oklch(from_var(--chip-color)_l_c_h/0.6)] dark:hover:bg-[oklch(from_var(--chip-color)_l_c_h/0.3)]',
								'light:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] light:bg-[oklch(from_var(--chip-color)_l_c_h/0.15)]',
								'light:hover:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] light:hover:bg-[oklch(from_var(--chip-color)_l_c_h/0.2)]',
								chipProps?.classes?.outlined,
							),
							filled: clsx(
								'dark:border-[oklch(from_var(--chip-color)_l_calc(c*0.8)_h/0.8)] dark:bg-[oklch(from_var(--chip-color)_calc(l*0.9)_c_h)]',
								'dark:hover:border-[oklch(from_var(--chip-color)_l_calc(c*0.8)_h/0.9)] dark:hover:bg-[oklch(from_var(--chip-color)_calc(l*1)_c_h)]',
								'light:border-[oklch(from_var(--chip-color)_calc(l*0.8)_calc(c*0.8)_h/0.7)] light:bg-[oklch(from_var(--chip-color)_l_c_h)]',
								'light:hover:border-[oklch(from_var(--chip-color)_calc(l*0.8)_calc(c*0.8)_h/0.8)] light:hover:bg-[oklch(from_var(--chip-color)_calc(l*1.13)_c_h)]',
								chipProps?.classes?.filled,
							),
						}}
						sx={[
							{
								'&.MuiChip-filled': {
									color: theme.palette.getContrastText(symbolColor),
									'--text-shadow-color': 'oklch(from currentcolor calc((l - 0.5) * 0.7 + 0.5) 0 h / 0.2)',
								},
							},
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							...(Array.isArray(chipProps?.sx) ? chipProps.sx : [chipProps?.sx]),
						]}
						onClick={handleClick}
						onDelete={xToDeselect ? handleDeselect : undefined}
						onContextMenu={handleRightClick}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					/>

					{hintPulse && !isHighlighted && <ChipInteractionHint />}
				</span>
			</Tooltip>

			<SmartPopover
				open={contextMenuOpen}
				anchorEl={contextMenuAnchorEl}
				onClose={handleContextMenuClose}
				directionMargin={350}
				style={{ zIndex: contextMenuZIndex }}
			>
				<ModelModuleChipContextMenu
					moduleInstanceName={moduleReferenceDesignatorProp}
					projectModule={projectModule}
					onZoomToItem={handleZoomToItem}
					onClose={handleContextMenuClose}
					renderAdditionalMenuItems={renderAdditionalMenuItems}
					relevantInstances={relevantInstances}
				/>
			</SmartPopover>
		</>
	);
}

/**
 * Returns whether any property/value pair in `target` is also present in `actual`.
 *
 * @param target - The chip's target graphics (its scoped highlight/selection map).
 * @param actual - The interaction manager's current highlight or selection map, if any.
 * @returns `true` if the maps share at least one property/value pair.
 */
function interactionMapOverlaps(
	target: Map<DrawingProperties, Set<DrawingPropertyValue>>,
	actual: Map<DrawingProperties, Set<DrawingPropertyValue>> | undefined,
): boolean {
	if (!actual) {
		return false;
	}
	for (const [property, values] of target) {
		const actualValues = actual.get(property);
		if (!actualValues) {
			continue;
		}
		for (const value of values) {
			if (actualValues.has(value)) {
				return true;
			}
		}
	}
	return false;
}
